import { CanvasTexture, RepeatWrapping, SRGBColorSpace, type Texture } from 'three';
import { mulberry32 } from '../utils/rng';

/**
 * Toutes les textures du jeu sont peintes en JavaScript dans un <canvas>.
 * Zero fichier a telecharger, et les motifs restent raccordables (tileable)
 * parce qu'ils n'utilisent que des periodes entieres.
 * cf. doc/01-rendu-et-couleur.md
 */

function canvas(w: number, h: number): CanvasRenderingContext2D {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const ctx = c.getContext('2d');
  if (!ctx) throw new Error('contexte 2D indisponible');
  return ctx;
}

/** Tache radiale douce : etincelles, fumee, poussiere, traceur. */
export function softSprite(): Texture {
  const S = 128;
  const ctx = canvas(S, S);
  const g = ctx.createRadialGradient(S / 2, S / 2, 0, S / 2, S / 2, S / 2);
  g.addColorStop(0, 'rgba(255,255,255,0.95)');
  g.addColorStop(0.35, 'rgba(255,255,255,0.32)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, S, S);
  const tex = new CanvasTexture(ctx.canvas);
  tex.colorSpace = SRGBColorSpace;
  return tex;
}

/**
 * Bouffee de fumee : plusieurs lobes decentres plutot qu'un disque.
 * Un sprite parfaitement radial se lit comme une bille, pas comme un nuage.
 */
export function smokeSprite(): Texture {
  const S = 128;
  const ctx = canvas(S, S);
  const rng = mulberry32(8812);
  for (let i = 0; i < 14; i++) {
    const r = 16 + rng() * 26;
    const x = S / 2 + (rng() - 0.5) * (S / 2 - r);
    const y = S / 2 + (rng() - 0.5) * (S / 2 - r);
    const g = ctx.createRadialGradient(x, y, 0, x, y, r);
    g.addColorStop(0, `rgba(255,255,255,${(0.1 + rng() * 0.14).toFixed(3)})`);
    g.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, S, S);
  }
  // Attenuation globale vers le bord : sinon les lobes sont coupes au carre
  // du sprite et la fumee a des angles droits.
  const mask = ctx.getImageData(0, 0, S, S);
  for (let y = 0; y < S; y++) {
    for (let x = 0; x < S; x++) {
      const d = Math.hypot(x - S / 2, y - S / 2) / (S / 2);
      const k = Math.max(0, 1 - d * d);
      const i = (y * S + x) * 4 + 3;
      mask.data[i] = Math.round(mask.data[i]! * k);
    }
  }
  ctx.putImageData(mask, 0, 0);
  const tex = new CanvasTexture(ctx.canvas);
  tex.colorSpace = SRGBColorSpace;
  return tex;
}

/**
 * Maillons de chenille, raccordables verticalement.
 *
 * C'est cette texture qui fait lire « chenille » plutot que « poutre
 * noire » : le defilement de ses UV est proportionnel a la distance
 * parcourue, donc la chenille avance exactement comme le char.
 * cf. doc/05-char-et-chenilles.md
 */
export function trackLinks(): Texture {
  const W = 64;
  const H = 128;
  /** 8 maillons sur la hauteur : periode entiere, donc motif raccordable. */
  const LINKS = 8;
  const pitch = H / LINKS;
  const ctx = canvas(W, H);

  ctx.fillStyle = '#17171a';
  ctx.fillRect(0, 0, W, H);

  for (let i = 0; i < LINKS; i++) {
    const y = i * pitch;
    // Corps du maillon, legerement bombe.
    const g = ctx.createLinearGradient(0, y, 0, y + pitch);
    g.addColorStop(0, '#3a3a3d');
    g.addColorStop(0.45, '#4c4c50');
    g.addColorStop(1, '#232326');
    ctx.fillStyle = g;
    ctx.fillRect(2, y + 1.5, W - 4, pitch - 3);

    // Jeu entre maillons : la ligne sombre qui donne l'echelle.
    ctx.fillStyle = '#0d0d0f';
    ctx.fillRect(0, y + pitch - 1.5, W, 1.5);

    // Boulons de part et d'autre du patin.
    ctx.fillStyle = '#5a5a5e';
    for (const bx of [8, W - 8]) {
      ctx.beginPath();
      ctx.arc(bx, y + pitch / 2, 2.1, 0, Math.PI * 2);
      ctx.fill();
    }
    // Guide central, celui qui encaisse le pignon.
    ctx.fillStyle = '#2b2b2e';
    ctx.fillRect(W / 2 - 4, y + 3, 8, pitch - 7);
  }

  const tex = new CanvasTexture(ctx.canvas);
  tex.wrapS = RepeatWrapping;
  tex.wrapT = RepeatWrapping;
  tex.colorSpace = SRGBColorSpace;
  return tex;
}

/** Cercles concentriques d'une cible de tir, facon panneau d'exercice. */
export function targetFace(): Texture {
  const S = 256;
  const ctx = canvas(S, S);
  ctx.fillStyle = '#e8e3d8';
  ctx.fillRect(0, 0, S, S);

  const rings = ['#c0392b', '#e8e3d8', '#c0392b', '#e8e3d8', '#c0392b'];
  rings.forEach((color, i) => {
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(S / 2, S / 2, (S / 2) * (1 - i * 0.18), 0, Math.PI * 2);
    ctx.fill();
  });

  // Centre noir : le point de visee, lisible meme a 250 metres dans la lunette.
  ctx.fillStyle = '#1b1b1b';
  ctx.beginPath();
  ctx.arc(S / 2, S / 2, S * 0.055, 0, Math.PI * 2);
  ctx.fill();

  // Cadre, pour que le panneau se detache du sol clair.
  ctx.strokeStyle = '#2d2d2d';
  ctx.lineWidth = 6;
  ctx.strokeRect(3, 3, S - 6, S - 6);

  const tex = new CanvasTexture(ctx.canvas);
  tex.colorSpace = SRGBColorSpace;
  tex.anisotropy = 4;
  return tex;
}

/**
 * Trace d'impact : tache sombre irreguliere, transparente sur les bords.
 *
 * Le canal alpha fait tout le travail. Un disque opaque pose sur le sol se
 * verrait comme une rondelle ; il faut une decroissance douce et des lobes
 * pour que ca passe pour une brulure.
 */
export function scorchDecal(): Texture {
  const S = 128;
  const ctx = canvas(S, S);
  const rng = mulberry32(44017);

  // Lobes sombres, de taille et de position variables.
  for (let i = 0; i < 26; i++) {
    const r = 14 + rng() * 30;
    const a = rng() * Math.PI * 2;
    const rad = rng() * (S / 2 - r) * 0.9;
    const x = S / 2 + Math.cos(a) * rad;
    const y = S / 2 + Math.sin(a) * rad;
    const g = ctx.createRadialGradient(x, y, 0, x, y, r);
    g.addColorStop(0, `rgba(22,18,14,${(0.2 + rng() * 0.3).toFixed(3)})`);
    g.addColorStop(1, 'rgba(22,18,14,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, S, S);
  }
  // Coeur plus noir : le point de detonation.
  const core = ctx.createRadialGradient(S / 2, S / 2, 0, S / 2, S / 2, S * 0.3);
  core.addColorStop(0, 'rgba(10,8,7,0.82)');
  core.addColorStop(1, 'rgba(10,8,7,0)');
  ctx.fillStyle = core;
  ctx.fillRect(0, 0, S, S);

  // Comme pour la fumee : on efface le bord du sprite, sinon la trace est
  // un carre.
  const img = ctx.getImageData(0, 0, S, S);
  for (let y = 0; y < S; y++) {
    for (let x = 0; x < S; x++) {
      const d = Math.hypot(x - S / 2, y - S / 2) / (S / 2);
      const k = 1 - d * d * d;
      const i = (y * S + x) * 4 + 3;
      img.data[i] = Math.round(img.data[i]! * Math.max(0, k));
    }
  }
  ctx.putImageData(img, 0, 0);

  const tex = new CanvasTexture(ctx.canvas);
  tex.colorSpace = SRGBColorSpace;
  return tex;
}
